import { useEffect } from "react";
import { useUiStore, type Toast } from "../store/uiStore.js";

/**
 * Toast 容器：右下角堆叠，最多 4 条，危险操作带上「重试 / 撤销」动作。
 *
 * 与乐观更新配合的关键点：失败 toast 必须包含**重试**按钮，
 * 成功 toast 在撤销窗口内必须包含**撤销**按钮 —— 否则用户只能靠记忆操作。
 */

const KIND_CLASS: Readonly<Record<Toast["kind"], string>> = {
  info: "border-line bg-raised text-ink",
  success: "border-success/40 bg-success-subtle text-success-text",
  warning: "border-warning/40 bg-warning-subtle text-warning-text",
  danger: "border-danger/40 bg-danger-subtle text-danger-text",
};

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: (id: string) => void }) {
  useEffect(() => {
    const timer = window.setTimeout(() => onDismiss(toast.id), toast.ttlMs);
    return () => window.clearTimeout(timer);
  }, [toast.id, toast.ttlMs, onDismiss]);

  return (
    <div
      role="status"
      aria-live="polite"
      className={`pointer-events-auto w-[360px] animate-toast-in rounded-lg border px-3 py-2 elev-lg ${KIND_CLASS[toast.kind]}`}
    >
      <div className="flex items-start justify-between gap-3">
        <p className="text-table font-medium">{toast.title}</p>
        <button
          type="button"
          onClick={() => onDismiss(toast.id)}
          className="shrink-0 text-meta opacity-70 transition-opacity dur-fast hover:opacity-100"
          aria-label="关闭提示"
        >
          ✕
        </button>
      </div>
      {toast.detail === null ? null : <p className="mt-1 text-meta opacity-90">{toast.detail}</p>}
      {toast.action === null ? null : (
        <button
          type="button"
          onClick={() => {
            toast.action?.run();
            onDismiss(toast.id);
          }}
          className="mt-2 rounded-control border border-current px-2 py-1 text-meta font-medium transition-opacity dur-fast hover:opacity-80"
        >
          {toast.action.label}
        </button>
      )}
    </div>
  );
}

export function Toaster() {
  const toasts = useUiStore((state) => state.toasts);
  const dismissToast = useUiStore((state) => state.dismissToast);

  if (toasts.length === 0) return null;

  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-[var(--z-toast)] flex flex-col gap-2">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={dismissToast} />
      ))}
    </div>
  );
}
